import { ApplicationRef, ComponentFactoryResolver, EmbeddedViewRef, Injectable, Injector, inject } from '@angular/core';
import { UpdateComponent } from '@frontend/shared/components/update.component';
import { Dialog } from '@frontend/shared/services/dialog.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Person, SystemOptions } from '@proman/interfaces/entity-interfaces';
import { CurrUser } from '@proman/interfaces/object-interfaces';
import { distinctUntilChanged, tap } from '@proman/rxjs-common';
import { Entity } from '@proman/services/entity.service';
import { CurrUserActions } from '@proman/store/curr-user';
import { getSystemOptions, SystemOptionsActions } from '@proman/store/system-options';
import { pipe, withLatestFrom } from 'rxjs';

import { EmployeeDocumentsReadDialogComponent } from '../../employees/components/employee-documents-read-dialog.component';
import { GetYourDomainReadyDialogComponent } from '../../employees/components/get-your-domain-ready-dialog.component';

@Injectable()
export class SystemOptionsFrontendEffects {
    private actions$ = inject(Actions);
    private componentFactoryResolver = inject(ComponentFactoryResolver);
    private applicationRef = inject(ApplicationRef);
    private injector = inject(Injector);
    private Dialog = inject(Dialog);
    private Entity = inject(Entity);
    private store = inject(Store);

    triggerUpdate$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(SystemOptionsActions.SetUpdateStatus),
                tap((action: { payload: boolean }) => {
                    const value = action.payload;
                    if (value) {
                        const hostView = this.componentFactoryResolver
                            .resolveComponentFactory(UpdateComponent)
                            .create(this.injector).hostView;

                        this.applicationRef.attachView(hostView);

                        document.body.appendChild((hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement);
                    }
                })
            ),
        { dispatch: false }
    );

    checkDocumentsRead$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(CurrUserActions.SetCurrUser),
                pipe(distinctUntilChanged()),
                withLatestFrom(this.store.select(getSystemOptions)),
                tap(([action]: [{ payload: CurrUser }, SystemOptions]) => {
                    const currUser: CurrUser = action.payload;
                    if (currUser) {
                        if (
                            currUser.type === 'employee' &&
                            this.userDocumentsCheck !== this.generatePersonName(currUser.person)
                        ) {
                            this.userDocumentsCheck = this.generatePersonName(currUser.person);
                            this.Entity.get('employee_document')
                                .getByEmployee(currUser.person.id)
                                .then(async (response) => {
                                    if (response) {
                                        const relevantIds = response.documents.map((doc) => doc.id);
                                        const relevantReads = response.reads.filter((read) =>
                                            relevantIds.includes(read.documentId)
                                        );
                                        if (response.documents.length > relevantReads.length) {
                                            const employee = await this.Entity.get('employee').get({
                                                id: currUser.person.id,
                                                join: ['documentsRead'],
                                            });
                                            this.Dialog.open(EmployeeDocumentsReadDialogComponent, { employee });
                                        }
                                    }
                                });
                        }
                    }
                })
            ),
        { dispatch: false }
    );

    checkDomainReady$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(CurrUserActions.SetCurrUser),
                pipe(distinctUntilChanged()),
                withLatestFrom(this.store.select(getSystemOptions)),
                tap(([action, systemOptions]: [{ payload: CurrUser }, SystemOptions]) => {
                    const currUser: CurrUser = action.payload;

                    if (!currUser || currUser.type !== 'employee' || !currUser.person?.isAdmin) {
                        return;
                    }

                    const userKey = this.generatePersonName(currUser.person);
                    const companyInfoMissing = false;
                        // !systemOptions?.organisationName ||
                        // !systemOptions?.organisationCode ||
                        // !systemOptions?.organisationAddress;

                    if (!companyInfoMissing) {
                        return;
                    }

                    this.userDomainReadyCheck = userKey;

                    this.Entity.get('employee')
                        .get({ id: currUser.person.id, join: ['documentsRead'] })
                        .then((employee) => {
                            this.Dialog.open(GetYourDomainReadyDialogComponent, { employee, systemOptions });
                        });
                })
            ),
        { dispatch: false }
    );

    generatePersonName = (person: Person) => `${person.firstName}${[person.lastName]}`;

    userDocumentsCheck: string;
    userDomainReadyCheck: string;
}
